import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';

import { getConfiguratonFile, getDashboardConfiguration } from '@/configs/helper';
import { requests } from '@/mixins/index';
import references from '@/configs/references.json';
import router from '@/router';

import { getAuthTokenPayload, getToken } from '@/utils/auth';
import { parseQueryParamsToFilters, constructQueryString } from '@/utils/search';
import { mergeCollaboratorScopes, hasWorkspaceAccess, hasRole } from '@/utils/collaborator';

import { DEFAULT_SORT as DEFAULT_VIVIER_SORT } from '@/utils/vivier';

Vue.use(Vuex);

const { VUE_APP_ENVIROMENT, VUE_APP_PREVIEW } = process.env;

const DEFAULT_SORT = [{ lastUpdateTime: { order: 'desc' } }];

const WORKSPACE_DEFAULT_ROUTES = {
  spontaneous: {
    name: 'candidature-spontanee-list',
    path: '/candidatures-spontanees'
  },
  centralizedWorkspace: {
    name: 'model-offers-list',
    path: '/modeles-d-offres'
  },
  default: {
    path: 'dashboard',
    name: 'home'
  }
};

const matchPath = (currentPath, allowedPath) => {
  // Exact match
  if (!allowedPath.includes('*')) {
    return currentPath === allowedPath;
  }
  // Wildcard match
  const base = allowedPath.replace('/*', '');
  return currentPath.startsWith(base);
};

const allowedPersistedSessionPaths = ['workspaces', 'rawContentTypes', 'references', 'activeBrand', 'activeWorkspace', 'activeContext', 'contextFacets', 'recentlyGeneratedOfferUuid'];

const store = new Vuex.Store({
  plugins: [
    createPersistedState({
      storage: window.sessionStorage,
      paths: allowedPersistedSessionPaths,
      getState: (key, storage) => {
        const raw = storage.getItem(key);
        if (!raw) return {};

        const parsed = JSON.parse(raw);

        // Remove anything not in paths
        return Object.fromEntries(Object.entries(parsed).filter(([k]) => allowedPersistedSessionPaths.includes(k)));
      }
    }),
    createPersistedState({
      storage: window.localStorage,
      paths: ['persistedActiveWorkspace']
    })
  ],
  state: {
    user: null,
    canWrite: false,
    loading: false,
    activeBrand: 'talent',
    activeWorkspace: null,
    persistedActiveWorkspace: null, // this way we can prevent the activeWorkspace to be changed when no need
    activeContext: null,
    contextFacets: null,

    // APP LOAD
    workspaces: [],
    rawContentTypes: null,
    references: null,

    // FILTERS & CONFIGS
    activeContentType: null,
    activeBoardConfiguration: null,
    appliedFilters: [],

    // DEFAULT DATA OF REQUEST
    total: 0,
    items: [],
    page: 1,
    itemsByPage: 10,
    filters: [],
    persistedFilters: [],
    filtersToKeep: [],
    query: null,
    sort: DEFAULT_SORT,
    vivierSort: DEFAULT_VIVIER_SORT,
    //! TODO use -> activeContext
    focusMode: false,
    // REQUEST DATA CONTROLLER
    abortController: null,

    appUrl: `${VUE_APP_PREVIEW}/recrutement`,
    snackbar: {},
    dialog: {},
    offer: {},
    offersUuidFilter: null,
    recentlyGeneratedOfferUuid: null,
    centralizedWorkspace: 'centralized_workspace',
    entities: []
  },
  getters: {
    contentTypeList: (_, getters) => {
      const { contentTypeList } = getDashboardConfiguration();
      if (!getters.canPerformAction('VIEW_VIVIER_PROSPECT')) {
        return contentTypeList.filter(menu => menu.id !== 'vivier');
      }
      return contentTypeList;
    },
    allowedWorkspaces: (state, getters) => {
      const { user, workspaces } = state;

      let filteredWorkspaces = workspaces;

      const hasWebtalesEmail = user.email && user.email.endsWith('@webtales.fr');
      if (!hasWebtalesEmail) {
        filteredWorkspaces = filteredWorkspaces.filter(w => w.code !== 'test_workspace');
      }

      if (getters.hasBasePermissions('ADMIN')) return filteredWorkspaces;

      if (!user?.readWorkspaces) return [];

      // If user has * access, return all workspaces except 'rh'
      if (user.readWorkspaces.includes('*')) {
        return filteredWorkspaces.filter(w => w.code !== 'rh');
      }

      // Filter workspaces by user permissions
      return filteredWorkspaces.filter(workspace => workspace.code !== 'rh'
        && user.readWorkspaces.includes(workspace.code));
    },
    // Base permission check
    hasBasePermissions: state => role => {
      const { user } = state;
      if (!user) return false;

      return hasRole(role, user.roles);
    },
    // Workspace access check
    hasWorkspaceAccess: state => (workspace, accessType = 'READ') => {
      const { user } = state;
      if (!user) return false;

      const spaces = accessType === 'READ' ? user.readWorkspaces : user.writeWorkspaces;

      return hasWorkspaceAccess(workspace, spaces, user.email);
    },
    // Route access check
    canAccessRoute: (state, getters) => path => {
      if (getters.hasBasePermissions('ADMIN')) return true;
      if (!getters.hasBasePermissions('RECRUITER')) return false;

      if (!getters.hasWorkspaceAccess(state.activeWorkspace, 'READ')) return false;

      const { user } = state;
      let recruiterPermissions = user?.permissions || [];

      if (getters.hasWorkspaceAccess('centralized_workspace', 'READ')) {
        recruiterPermissions = [...recruiterPermissions, 'VIEW_MODEL_OFFER', 'CREATE_MODEL_OFFER', 'EDIT_MODEL_OFFER'];
      }

      // Check if user has any recruiter permissions
      if (!recruiterPermissions?.length) return false;

      // Check route against permissionRoutes
      return Object.entries(references.permissionRoutes).some(([permission, basePaths]) => {
        if (!recruiterPermissions.includes(permission)) return false;
        return basePaths.some(basePath => matchPath(path, basePath));
      });
    },
    // Action permission check
    canPerformAction: (state, getters) => action => {
      if (getters.hasBasePermissions('ADMIN')) return true;
      if (!getters.hasBasePermissions('RECRUITER')) return false;

      if (!getters.hasWorkspaceAccess(state.activeWorkspace, 'WRITE')) return false;

      const { user } = state;
      let userPermissions = user.permissions.map(permission => permission.toUpperCase());
      const requestedAction = action.toUpperCase();

      if (getters.hasWorkspaceAccess('centralized_workspace', 'WRITE')) {
        userPermissions = [...userPermissions, 'VIEW_MODEL_OFFER', 'CREATE_MODEL_OFFER', 'EDIT_MODEL_OFFER'];
      }

      return userPermissions.includes(requestedAction);
    },
    isCentralizedWorkspace: state => state.activeWorkspace === state.centralizedWorkspace,
    getActiveWorkspaceUuid: state => {
      const workspace = state.workspaces.find(w => w.code === state.activeWorkspace);

      return workspace ? workspace.settings.entityUuid : null;
    },
    getWorkspaceCode: state => entityUuid => {
      const workspace = state.workspaces.find(w => w.settings.entityUuid === entityUuid);

      return workspace.code;
    },
    getAppUrl (state) {
      return state.appUrl;
    },
    getLoading (state) {
      return state.loading;
    },
    getFocusMode (state) {
      return state.focusMode;
    },
    getQuery (state) {
      return state.query;
    },
    getTotal (state) {
      return state.total;
    },
    getTableItems (state) {
      return state.items;
    },
    getFiltersConfig (state) {
      return state.filters.filter(f => f && (!f.workspaces || f.workspaces.includes(state.activeWorkspace)));
    },
    getActiveBrand (state) {
      return state.activeBrand;
    },
    getActiveFiltersCount: state => Object.values(state.appliedFilters).reduce((acc, filter) => acc + filter.values.length, 0),
    getActiveFilter: state => attribute => state.appliedFilters.find(f => f.attribute === attribute),
    isActiveFilter: state => (attribute, value) => {
      const filter = state.appliedFilters.find(f => f.attribute === attribute);
      if (!filter) return false;

      return filter.values.some(filterValue => `${filterValue}` === `${value}`);
    },
    getActiveWorkspace (state) {
      return state.activeWorkspace;
    },
    getActiveContext (state) {
      return state.activeContext;
    },
    getContextFacets (state) {
      return state.contextFacets;
    },
    getBrandUrl: () => (VUE_APP_ENVIROMENT === 'prod' ? 'https://asso.ucpa.com' : 'https://recette.asso.ucpa.com'),
    allowAction: state => contentRoles => {
      const userRoles = state.user.roles || [];
      if (userRoles.includes('admin')) return true;

      return contentRoles.some(contentRole => userRoles.includes(contentRole));
    },
    allowField: state => contentHideField => {
      const filterFounded = state.filters.find(filter => filter.field === contentHideField.fieldName);
      return !(filterFounded && contentHideField.values.includes(filterFounded.value));
    },
    getDashboardConfigurationByLabel: (_, getters) => label => (getters.contentTypeList?.find(contentType => contentType.label === label) || null),
    getDashboardConfigurationByWtype: (_, getters) => wtype => (getters.contentTypeList?.find(contentType => contentType.wtype === wtype) || null),
    getSortValueByKey: state => key => {
      const sortItem = state.sort.find(s => s[key]);

      return sortItem ? sortItem[key] : null;
    },
    getVivierSort: state => state.vivierSort
  },
  actions: {
    async initializeUser ({ commit }) {
      const token = getToken();

      if (!token) {
        commit('setUser', null);
        return false;
      }

      const user = await getAuthTokenPayload(token, window.location.origin);

      const { success, data } = await requests.getCollaborator();

      if (success) {
        const { readWorkspaces, writeWorkspaces, permissions } = mergeCollaboratorScopes(data);

        commit('setUser', {
          ...data,
          name: `${data.firstname} ${data.lastname}`,
          roles: user.roles,
          readWorkspaces,
          writeWorkspaces,
          permissions
        });

        return true;
      }

      commit('setUser', null);

      return false;
    },
    getOffer: async ({ commit, getters }, { uuid }) => {
      const response = await requests.getOffer(uuid);

      if (!response.success || !response.data) {
        router.push({ name: 'error', params: { errorCode: 404 } });
        return null;
      }

      const { data } = response;
      const { workspace } = data;

      if (!getters.hasWorkspaceAccess(workspace, 'READ')) {
        router.push({ name: 'error', params: { errorCode: 401 } });
        return null;
      }

      commit('setActiveWorkspace', workspace);
      commit('setOffer', data);

      return null;
    },
    applyFilter: async ({ state, commit, dispatch }, { attribute, value }) => {
      if (state.abortController) {
        state.abortController.abort();
        commit('setAbortController', null);
      }

      const index = state.appliedFilters.findIndex(a => a.attribute === attribute);
      const configuration = state.activeBoardConfiguration.filters.find(a => a.attribute === attribute);

      // IF ALREADY EXIST REMOVE IT
      if (index >= 0 && state.appliedFilters[index].values.includes(value)) {
        const newValues = state.appliedFilters[index].values.filter(v => v !== value);
        commit('setFiltersToKeep', newValues.length === 0 ? [] : [attribute]);

        dispatch('removeFilterAndSearch', { attribute, value });
        return;
      }

      if (configuration.isMultiple) {
        if (index >= 0) {
          const newValues = [...state.appliedFilters[index].values, value];
          commit('updateAppliedFilterValues', { index, values: newValues });
        } else {
          commit('addAppliedFilter', { attribute, values: [].concat(value) });
        }
      } else if (index >= 0) {
        commit('updateAppliedFilterValues', { index, values: [].concat(value) });
      } else {
        commit('addAppliedFilter', { attribute, values: [].concat(value) });
      }

      // const params = await dispatch('prepareActiveParamsForURL');
      await dispatch('updateURL');

      commit('setFiltersToKeep', [attribute]);

      dispatch('searchData');
    },
    getAppLoad: async ({ state, commit }) => {
      if ([state.workspaces.length, state.rawContentTypes, state.references].every(Boolean)) {
        return;
      }

      commit('setLoading');

      try {
        const response = await requests.getAppLoad();
        const { success, message, data } = response;

        if (!success) {
          commit('setSnackbar', { text: `Error fetching app load: ${message}`, color: 'error' });
          console.error(`Error fetching app load: ${message}`);
        } else {
          commit('setAppLoad', data);
        }
      } catch (error) {
        commit('setSnackbar', { text: 'Error fetching app load', color: 'error' });
        console.error('Error fetching app load:', error);
      } finally {
        commit('setLoading', false);
      }
    },
    updateURL: async ({ state, dispatch }) => {
      const params = {
        filters: state.appliedFilters || [],
        query: state.query?.trim(),
        sort: state.sort || [],
        page: state.page
      };

      await dispatch('constructURL', { activeParams: params });
    },
    parseUrlData: ({ state, commit }, queryParams) => {
      const { filters, sort, query, page } = parseQueryParamsToFilters(queryParams, state.activeBoardConfiguration.dashboardFields);
      if (filters) {
        commit('_setAppliedFilters', filters);
      }

      if (sort) {
        commit('_setSort', sort);
      }

      if (query) {
        commit('_setQuery', query);
      }

      if (page) {
        commit('_setPage', page);
      }
    },
    constructURL: (_, { activeParams }) => {
      const queryParams = constructQueryString(activeParams);

      window.history.pushState(
        {},
        null,
        `${router.currentRoute.path}${queryParams ? '?' : ''}${queryParams}`
      );
    },
    setQuery: async ({ commit, dispatch }, query) => {
      commit('_setQuery', query);
      commit('_setPage', 1);

      await dispatch('updateURL');

      dispatch('searchData');
    },
    setPage: async ({ commit, dispatch }, page) => {
      commit('_setPage', page);

      await dispatch('updateURL');

      dispatch('searchData');
    },
    setSort: async ({ commit, dispatch }, sort) => {
      commit('_setSort', sort);

      await dispatch('updateURL');

      dispatch('searchData');
    },
    clearFiltersAndSearch: async ({ commit, dispatch }) => {
      commit('_removeFilters');
      commit('_setPage', 1);

      await dispatch('updateURL');

      dispatch('searchData'); // Adjust the query parameter as needed
    },
    removeFilterAndSearch: async ({ commit, dispatch }, { attribute, value }) => {
      commit('_removeFilter', { attribute, value });
      commit('_setPage', 1);

      await dispatch('updateURL');

      dispatch('searchData'); // Adjust the query parameter as needed
    },
    searchData: async ({ state, commit, getters, dispatch }) => {
      if (!state.workspaces.length) { return; }

      commit('setLoading');
      let collapse = null;

      if (state.activeContentType === 'application') {
        const uuid = router.currentRoute.params.offerUuid;

        if (uuid?.startsWith('offer_')) {
          // dispatch('resetFilters');
          await dispatch('getOffer', { uuid });
        }

        const createTimeSort = state.sort.find(s => s.createTime);

        let createTimeOrder = createTimeSort?.createTime.order;

        if (!state.items.length) {
          createTimeOrder = 'desc';
        }

        const activeWorkspaceUuid = getters.getActiveWorkspaceUuid;
        const filters = [];
        if (activeWorkspaceUuid) {
          if (router.currentRoute.name !== 'candidatures') {
            filters.push({ bool: { must_not: [{ term: { canceled: true } }] } });
          }

          if (uuid) {
            if (uuid.startsWith('collaborator_')) {
              filters.push({ term: { 'collaborator.uuid': uuid } });
            } else if (uuid.startsWith('offer_') && state.offer?.uuid) {
              filters.push(
                { term: { 'entity.uuid': activeWorkspaceUuid } },
                { term: { 'offer.uuid': state.offer.uuid } }
              );
            }
          }

          if (router.currentRoute.name === 'candidatures') {
            filters.push({ term: { 'entity.uuid': activeWorkspaceUuid } });
          }

          commit('setPersistedFilters', filters);
        } else if (state.activeWorkspace === 'spontaneous') {
          commit('setPersistedFilters', ([
            { term: { isSpontaneousApplication: true } }
          ]));
        }

        state.sort = [
          {
            _script: {
              type: 'number',
              order: 'asc',
              script: {
                source: `
                  if (doc['recruiterStatus'].size() == 0) {
                    return 999;   // default when field is missing
                  }
                  Map statusOrder = [
                    'HIRED':0,
                    'TO_SORT':1,
                    'TO_PRE_QUALIFY':2,
                    'TO_MEET':3,
                    'MET_VALIDATION':4,
                    'RECOMMENDED':5,
                    'RECOMMENDATION_APPROVED':6,
                    'SEND_PROMISE_OF_EMPLOYMENT':7,
                    'PROMISE_OF_EMPLOYMENT_SENT':8,
                    'PROMISE_APROVED':9,
                    'VALIDATED':10,
                    'IN_PROCESS':11,
                    'KEEP_IN_TOUCH':12,
                    'PROMISE_REJECTED':13,
                    'RECOMMENDATION_REJECTED':14,
                    'WITHDRAWN':15,
                    'REJECTED':16
                  ];
                  return statusOrder.getOrDefault(doc['recruiterStatus'].value, 999);
                `,
                lang: 'painless'
              }
            }
          },
          { stared: { order: createTimeOrder || 'desc' } },
          { createTime: { order: createTimeOrder || 'asc' } }
        ];

        filters.push({ exists: { field: 'collaborator.uuid' } });

        if (state.activeBoardConfiguration.listComponent === 'CandidateView') {
          collapse = { field: 'collaborator.uuid' };
          filters.push(
            { bool: { must_not: [{ term: { ucpaExperience: true } }] } },
            { term: { 'entity.uuid': activeWorkspaceUuid } }
          );
        }
      } else {
        commit('setPersistedFilters', ([]));

        if (!state.sort.length || state.sort.some(s => 'stared' in s)) {
          commit('_setSort', DEFAULT_SORT);
        }
      }

      commit('setAbortController', new AbortController());

      state.offersUuidFilter = null;
      const foundedUuidFilter = state.appliedFilters.find(f => f.attribute === 'showOffers');
      if (router.currentRoute.name === 'offers' && foundedUuidFilter) {
        const applicationStats = await requests.searchOpenSearchStatistics({
          key: 'OFFERS_WITH_APPLICATIONS',
          entityUuid: getters.getActiveWorkspaceUuid
        });
        if (applicationStats.total) {
          const queryClause = foundedUuidFilter.values[0] === 'avec-candidature' ? 'should' : 'must_not';
          state.offersUuidFilter = { bool: { [queryClause]: applicationStats.aggregations.offersWithApplications.buckets.map(bucket => ({ match: { 'uuid.raw': bucket.key } })) } };
        }
      }

      try {
        if (!state.activeContentType) {
          throw new Error('activeContentType is not defined');
        }

        const activeConfiguration = getters.getDashboardConfigurationByWtype(state.activeContentType);

        const response = await requests.searchOpenSearchContent({
          workspace: activeConfiguration?.workspace || state.activeWorkspace,
          offersUuidFilter: state.offersUuidFilter,
          wtype: state.activeContentType,
          filtersConfiguration: state.activeBoardConfiguration.filters.filter(f => !f.workspaces || f.workspaces.includes(state.activeWorkspace)),
          activeFilters: state.appliedFilters.filter(f => !['showOffers'].includes(f.attribute)),
          query: state.query,
          from: (state.page - 1) * state.itemsByPage,
          size: state.itemsByPage,
          sort: state.sort,
          ...(collapse ? { collapse } : {}),
          indice: activeConfiguration?.osIndex,
          persistedFilters: state.persistedFilters,
          signal: state.abortController.signal
        });

        const { success, message, items, total, filters } = response;

        if (!success) {
          // SHOW ERROR MESSAGE
          // commit('setSnackbar', { text: `Erreur dans la recherche de données : ${message}`, color: 'error' });
          console.error(`Error searching data: ${message}`);
        } else {
          if (state.filtersToKeep.length > 0) {
            const newFilters = state.filters.map(f => {
              // Keep the original filter configuration if it's in filtersToKeep
              if (state.filtersToKeep.includes(f.attribute)) {
                // Merge with new filter data if available
                const newFilterData = filters.find(newFilter => newFilter.attribute === f.attribute);
                return newFilterData ? { values: newFilterData.values, ...f } : f;
              }
              // Otherwise use the new filter data, falling back to the existing
              // config so we never inject undefined entries into state.filters
              return filters.find(newFilter => newFilter.attribute === f.attribute) || f;
            }).filter(Boolean);
            commit('setFilters', newFilters);
          } else {
            commit('setFilters', filters);
          }

          commit('setItems', items);
          commit('setTotal', total);
        }
      } catch (error) {
        commit('setSnackbar', { text: 'Erreur dans la recherche de données', color: 'error' });
        console.error('Error searching data:', error);
      } finally {
        commit('setAbortController', null);
        commit('setLoading', false);
      }
    },
    setConfigurationsByWtype: ({ commit, getters, dispatch }, { wtype, forceSearchData = false }) => {
      const config = getters.getDashboardConfigurationByWtype(wtype);

      if (!config) {
        console.error(`Configuration not found for wtype: ${wtype}`);
        commit('setSnackbar', { text: `Configuration non trouvée pour wtype : ${wtype}`, color: 'error' });

        return;
      }

      commit('setActiveContentType', wtype);

      // GET DASHBOARD CONFIG BY THE FILE
      dispatch('applyActiveBoardConfiguration', { configFile: config.configFile, forceSearchData });
    },
    setConfigurationsByLabel: ({ commit, getters, dispatch }, { label, forceSearchData = false }) => {
      if (!label) return;

      const config = getters.getDashboardConfigurationByLabel(label);

      if (!config) {
        console.error(`Configuration not found for label: ${label}`);
        // commit('setSnackbar', { text: `Configuration non trouvée pour l'étiquette : ${label}`, color: 'error' });

        return;
      }

      commit('setActiveContentType', config.wtype);

      // GET DASHBOARD CONFIG BY THE FILE
      dispatch('applyActiveBoardConfiguration', { configFile: config.configFile, forceSearchData });
    },
    applyActiveBoardConfiguration: async ({ commit, dispatch, getters }, { configFile, forceSearchData = false }) => {
      if (getters.isCentralizedWorkspace && configFile === 'offerLayoutConfig.json') {
        configFile = 'centralizedOfferLayoutConfig.json';
      }
      const config = getConfiguratonFile(configFile);

      commit('setActiveBoardConfiguration', config);

      if (forceSearchData) {
        dispatch('searchData');
      }
    },
    updateWorkspace ({ state, commit, getters, dispatch }, workspace) {
      if (!workspace) {
        return {
          path: 'dashboard',
          name: 'home'
        };
      }

      const currentRouteName = router.currentRoute.name;
      const lastWorkspaceWasCentralized = getters.isCentralizedWorkspace;

      dispatch('resetFilters');
      commit('setActiveWorkspace', workspace);

      if (lastWorkspaceWasCentralized && currentRouteName === 'offers') {
        dispatch('setConfigurationsByWtype', { wtype: state.activeContentType });
      }

      // Get default route for workspace
      let nextRoute = WORKSPACE_DEFAULT_ROUTES.default;

      if (workspace === this.state.centralizedWorkspace) {
        nextRoute = WORKSPACE_DEFAULT_ROUTES.centralizedWorkspace;

        // Check if user has access to default route
        const hasAccess = getters.canAccessRoute(nextRoute.path);

        return hasAccess ? nextRoute : {
          name: 'error',
          params: { errorCode: 403 }
        };
      }

      if (workspace === 'spontaneous') {
        nextRoute = WORKSPACE_DEFAULT_ROUTES.spontaneous;

        // Check if user has access to default route
        const hasAccess = getters.canAccessRoute(nextRoute.path);

        return hasAccess ? nextRoute : {
          name: 'error',
          params: { errorCode: 403 }
        };
      }

      const isSpontaneousRoute = ['candidature-spontanee-list', 'candidature-spontanee'].includes(currentRouteName);

      const isCentralizedRoute = ['model-offers-list', 'model_offer'].includes(currentRouteName);

      if (isSpontaneousRoute || isCentralizedRoute) {
        return nextRoute;
      }

      return currentRouteName === 'error' ? nextRoute : null;
    },
    resetFilters ({ state, commit }) {
      // Check routes logic, some filters should maintain status
      if (!['offer', 'job'].includes(state.activeContentType)) {
        commit('_setQuery', null);
      }

      // const fromStatistics = router.currentRoute.from?.name === 'dashboard';
      // if (!router.currentRoute.name === 'candidatures') {
      commit('_setAppliedFilters', []);
      // }
      commit('_setPage', 1);
      commit('_setSort', DEFAULT_SORT);
      commit('setTotal', 0);
      commit('setItems', []);
      commit('setItemsByPage', 10);
      commit('setFilters', []);
      commit('setFiltersToKeep', []);
      commit('setPersistedFilters', []);
    }
  },
  mutations: {
    _setQuery (state, query) {
      state.query = query;
    },
    _setAppliedFilters (state, filters) {
      state.appliedFilters = filters;
    },
    _setPage (state, page) {
      state.page = page;
    },
    _removeFilters (state) {
      state.appliedFilters = [];
    },
    _setSort (state, sort) {
      state.sort = sort;
    },
    _removeFilter (state, { attribute, value }) {
      const filter = state.appliedFilters.find(a => a.attribute === attribute);

      if (filter && value) {
        filter.values = filter.values.filter(v => v !== value);
        if (filter.values.length === 0) {
          state.appliedFilters = state.appliedFilters.filter(a => a.attribute !== attribute);
        }
      } else {
        state.appliedFilters = state.appliedFilters.filter(a => a.attribute !== attribute);
      }
    },
    setAbortController (state, abortController) {
      state.abortController = abortController;
    },
    setLoading (state, show = true) {
      state.loading = show;
    },
    toggleFocusMode (state) {
      state.focusMode = !state.focusMode;
    },
    updateAppliedFilterValues (state, { index, values }) {
      state.page = 1;
      Vue.set(state.appliedFilters[index], 'values', values);
    },
    setFiltersToKeep (state, filtersToKeep) {
      state.filtersToKeep = filtersToKeep;
    },
    addAppliedFilter (state, filter) {
      state.page = 1;
      state.appliedFilters.push(filter);
    },
    setActiveBoardConfiguration (state, activeBoardConfiguration) {
      state.activeBoardConfiguration = activeBoardConfiguration;
      state.filters = activeBoardConfiguration.filters;
    },
    setFilters (state, filters) {
      state.filters = filters;
    },
    setItems (state, items) {
      state.items = items;
    },
    setTotal (state, total) {
      state.total = total;
    },
    setAppLoad (state, data) {
      const { workspaces, shema } = data;

      state.workspaces = workspaces.items.filter(workspace => workspace.code !== 'rh').sort((a, b) => a.name.localeCompare(b.name));

      if (state.persistedActiveWorkspace) {
        state.activeWorkspace = state.persistedActiveWorkspace;
      } else {
        state.activeWorkspace = state.workspaces[0]?.code; // TODO: check if is allowed workspace
        state.persistedActiveWorkspace = state.activeWorkspace;
      }

      state.rawContentTypes = shema.types;
      state.references = { ...shema.references, ...references };
    },
    setActiveContentType (state, activeContentType) {
      state.activeContentType = activeContentType;
    },
    setActiveBrand (state, activeBrand) {
      state.activeBrand = activeBrand;
    },
    setActiveWorkspace (state, activeWorkspace) {
      state.activeWorkspace = activeWorkspace;
      state.persistedActiveWorkspace = activeWorkspace;
    },
    setActiveContext (state, activeContext) {
      state.activeContext = activeContext;
    },
    setContextFacets (state, contextFacets) {
      state.contextFacets = contextFacets;
    },
    setUser (state, user) {
      state.user = user;
    },
    setItemsByPage (state, itemsByPage) {
      state.itemsByPage = itemsByPage;
    },
    setSnackbar (state, payload) {
      state.snackbar = {
        show: true,
        ...payload
      };
    },
    setDialog (state, payload) {
      state.dialog = payload;
    },
    resetSnackbar (state) {
      state.snackbar = {};
    },
    resetDialog (state) {
      state.dialog = {};
    },
    setPersistedFilters (state, persistedFilters) {
      state.persistedFilters = persistedFilters;
    },
    setOffer (state, offer) {
      state.offer = offer;
    },
    setRecentlyGeneratedOfferUuid (state, generatedOfferUuid) {
      state.recentlyGeneratedOfferUuid = generatedOfferUuid;
    },
    setEntities (state, entities) {
      state.entities = entities;
    },
    setVivierSort (state, sort) {
      if (!sort) {
        state.vivierSort = DEFAULT_VIVIER_SORT;
      }

      state.vivierSort = sort;
    }
  }
});

export default store;
